/**
 * End-of-feed screen — web port of iOS `playground/EndOfContentScreen.tsx`,
 * shown as the final vertical panel once the feed is exhausted
 * (`feedHasMore$` is false and the user reaches the last chapter).
 */
import { useEffect } from "react";
import { colors } from "@/ui";
import parrotLogo from "@/assets/parrot-bird.png";
import { VIDEO_MAX_W } from "./videoConstants";
import { TopBar, TOP_BAR_HEIGHT } from "../components/ui/TopBar";
import { useTrackFeedEvents } from "../hooks/useTrackFeedEvents";

const FEEDBACK_CALL_URL = "https://cal.com/julia-hudea-2qwned/parrot-feedback";

export function EndOfContentScreen() {
  const { trackEvent } = useTrackFeedEvents();

  useEffect(() => {
    trackEvent("end_of_content_viewed");
  }, [trackEvent]);

  return (
    <div className="h-full w-full flex flex-col items-center bg-black">
      <div className="fixed top-0 left-0 right-0 z-40 flex justify-center pointer-events-none">
        <div className={`w-full ${VIDEO_MAX_W} pointer-events-auto`}>
          <TopBar />
        </div>
      </div>
      <div className="shrink-0" style={{ height: TOP_BAR_HEIGHT }} />

      <div
        className={`w-full flex-1 min-h-0 flex flex-col items-center justify-center px-8 pb-24 text-center gap-10 ${VIDEO_MAX_W}`}
      >
        <h2 className="text-white text-2xl font-bold leading-tight">
          Whoa, you made it to the end!
        </h2>

        {/* Assistant ChatBubble with a downward (middle-bottom) tail — mirrors
            iOS ChatBubble variant="assistant" position="middle-bottom". */}
        <div className="relative flex flex-col items-center">
          <div
            className="rounded-[20px] px-4 py-3 max-w-[320px]"
            style={{
              backgroundColor: colors.white,
              boxShadow: "0 1px 8px rgba(0,0,0,0.05)",
            }}
          >
            <p
              className="text-base font-medium leading-6"
              style={{ color: colors.text.primary }}
            >
              You just scrolled through everything we&apos;ve got. Thank you for
              being one of the first to try our new feature. We&apos;d love to
              hear what you think so we can make it even better.
            </p>
          </div>
          {/* Two-layer tail (border + fill) pointing down, matches iOS. */}
          <div
            aria-hidden
            style={{
              width: 0,
              height: 0,
              borderLeft: "9px solid transparent",
              borderRight: "9px solid transparent",
              borderTop: "9px solid #e5e7eb",
              marginTop: -1,
            }}
          />
          <div
            aria-hidden
            style={{
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderTop: `8px solid ${colors.white}`,
              marginTop: -9,
            }}
          />
        </div>

        {/* Parrot logo (iOS Logo size="xlarge" 64px scaled 2 ≈ 128px). */}
        <img
          src={parrotLogo}
          alt="Parrot"
          className="w-32 h-32 object-contain"
        />

        <div className="w-full flex flex-col items-center mt-12">
          <a
            href={FEEDBACK_CALL_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackEvent("end_of_content_cta_clicked")}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm text-white active:scale-95 transition-transform"
            style={{ backgroundColor: "var(--color-blue)" }}
          >
            Book a call with Julia ↗
          </a>

          <p className="text-white/40 text-xs mt-4 max-w-[300px] leading-relaxed">
            (More content is available from the courses tab, and more coming
            here soon.)
          </p>
        </div>
      </div>
    </div>
  );
}
